import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";
import { optimizeInfo, solution, routificSetting } from "../../models";

import {
  deliveryManifest,
  deliverySettings,
  manifest,
  manifestLine,
  storageAddress,
  unserved,
} from "../../models/DeliveryManifest";

import { fetchoptimizeByJobIdAsync } from "./sliceOptimizeServices";
import {
  fetchAllManifestByDateAsync,
  fetchDeliverySettings,
  fetchStorageAddressAsync,
  saveStorageAddress,
  getRoutificDefaultSettings,
  updateRoutificDefaultSettings,
} from "./sliceServices";

const initialState = {
  deliveryManifest: {} as deliveryManifest,
  unallocatedDocuments: [] as manifestLine[],
  loading: false,
  loaded: false,
  storageAddress: {} as storageAddress,
  storageAddressLoading: false,
  storageAddressLoaded: false,

  manifestETA: [] as any,

  routificSettings: {} as routificSetting,
  routificSettingsLoading: false,
  routificSettingsLoaded: false,

  deliverySettings: {} as deliverySettings,
  deliverySettingsLoading: false,
  deliverySettingsLoaded: false,

  optimizingGroups: [] as {
    estimateTimeInterval: number;
    jobId: string;
    manifests: manifest[];
    optimizeInfo: optimizeInfo[];
    unserved?: unserved[];
    solution?: solution[];
    manuallyChanged?: boolean;
  }[],
};

const manifestSlice = createSlice({
  name: "manifest",
  initialState,
  reducers: {
    clearManifest: (state) => {
      state.deliveryManifest = {} as deliveryManifest;
      state.optimizingGroups = [] as any;
      state.loading = false;
      state.loaded = false;
    },
    moveRoute: (state, action: PayloadAction<any>) => {
      const { manifestTarget, position, manifestLineMoving } = action.payload;
      const addManifestLines = cloneDeep(manifestTarget.manifestLines);
      addManifestLines.splice(position - 1, 0, manifestLineMoving);
      addManifestLines.map((item: manifestLine, index: number) => {
        item.position = index;
        return item;
      });

      state.deliveryManifest.manifests = state.deliveryManifest.manifests.map(
        (manifest) => {
          if (manifest.id === manifestTarget.id) {
            manifest.manifestLines = addManifestLines;
          }
          return manifest;
        }
      );
    },
    replaceManifestsUpdatedETAs: (state, action: PayloadAction<any>) => {
      state.deliveryManifest.manifests = action.payload;
    },
    updateUnallocatedDocuments: (
      state,
      action: PayloadAction<manifestLine[]>
    ) => {
      state.unallocatedDocuments = state.unallocatedDocuments
        .filter((doc) => action.payload.some((d) => d.id === doc.id))
        .concat(
          action.payload.filter(
            (d) => !state.unallocatedDocuments.some((doc) => doc.id === d.id)
          )
        );

      state.deliveryManifest.unallocatedDocumentCount =
        state.unallocatedDocuments.length;
    },
    addManifests: (state, action: PayloadAction<{ manifests: manifest[] }>) => {
      const { manifests } = action.payload;
      if (manifests.length) {
        state.deliveryManifest.manifests = [
          ...state.deliveryManifest.manifests,
          ...manifests.filter(
            (manifest) =>
              !state.deliveryManifest.manifests.some(
                (m) => m.id === manifest.id
              )
          ),
        ];
      }
    },
    updateManifests: (state, action: PayloadAction<any>) => {
      const { manifests, unallocatedDocumentCount } = action.payload;
      if (manifests.length) {
        state.deliveryManifest.manifests = state.deliveryManifest.manifests.map(
          (manifest) =>
            manifests.find(
              (newManifest: manifest) =>
                newManifest && newManifest.id === manifest.id
            ) || manifest
        );
      }
      if (
        unallocatedDocumentCount &&
        state.deliveryManifest.unallocatedDocumentCount >= 0
      ) {
        state.deliveryManifest.unallocatedDocumentCount =
          state.deliveryManifest.unallocatedDocumentCount +
          unallocatedDocumentCount;
      }
    },
    updateETAIntoManifestItem: (state, action: PayloadAction<any>) => {
      state.deliveryManifest.manifests = state.deliveryManifest.manifests.map(
        (manifest: manifest) =>
          manifest.id === action.payload.id ? action.payload : manifest
      );
    },
    updateRoutificSettings: (state, action: PayloadAction<any>) => {
      state.routificSettings = {
        ...state.routificSettings,
        ...action.payload,
      };
    },
    addOptimizeGroups: (state, action: PayloadAction<any>) => {
      const { optimizeRequestData, items } = action.payload;
      state.optimizingGroups = [
        ...state.optimizingGroups,
        {
          optimizeInfo: items,
          estimateTimeInterval: optimizeRequestData.estimateTimeInterval,
          jobId: optimizeRequestData.jobId,
          manifests: state.deliveryManifest.manifests
            .filter((manifest) =>
              items.find((it: optimizeInfo) => it.manifestId === manifest.id)
            )
            .sort((a, b) => {
              if (a.route && b.route) {
                return a.route.name.localeCompare(b.route.name);
              }
              return 0;
            }),
        },
      ];

      state.deliveryManifest = {
        ...state.deliveryManifest,
        manifests: state.deliveryManifest.manifests.filter(
          (manifest) =>
            !items.find((it: optimizeInfo) => it.manifestId === manifest.id)
        ),
      };
    },
    addOptimizedGroupsInfo: (state, action: PayloadAction<any>) => {
      const { fetchOptimizeByJobIdData, jobId } = action.payload;
      state.optimizingGroups = state.optimizingGroups.map(
        (optimizingGroup: any) => {
          if (optimizingGroup.jobId === jobId) {
            const rs = {
              ...optimizingGroup,
              ...fetchOptimizeByJobIdData,
            };
            return rs;
          } else {
            return optimizingGroup;
          }
        }
      );
    },
    discardOptimizeGroup: (state, action: PayloadAction<any>) => {
      const { jobId, manifests } = action.payload;

      state.optimizingGroups = state.optimizingGroups.filter(
        (optimizingGroup: any) => optimizingGroup.jobId !== jobId
      );
      state.deliveryManifest = {
        ...state.deliveryManifest,
        manifests: manifests.concat(state.deliveryManifest.manifests),
      };
    },
    applyOptimizeGroup: (state, action: PayloadAction<any>) => {
      const { jobId, manifests, unallocatedDocumentCount } = action.payload;
      state.optimizingGroups = state.optimizingGroups.filter(
        (optimizingGroup: any) => optimizingGroup.jobId !== jobId
      );
      state.deliveryManifest = {
        ...state.deliveryManifest,
        manifests: manifests.concat(state.deliveryManifest.manifests),
      };
      state.deliveryManifest.unallocatedDocumentCount =
        (state.deliveryManifest.unallocatedDocumentCount || 0) +
        (unallocatedDocumentCount || 0);
    },
    moveUnallocatedManifestLineInOptimizeGroup: (
      state,
      action: PayloadAction<any>
    ) => {
      const {
        manifestLineMoving,
        targetManifest,
        targetPosition,
        jobId,
      }: {
        manifestLineMoving: manifestLine;
        targetManifest: manifest;
        targetPosition: number;
        jobId: string;
      } = action.payload;

      state.optimizingGroups = state.optimizingGroups.map((optimizingGroup) => {
        if (optimizingGroup.jobId === jobId) {
          optimizingGroup.solution = optimizingGroup.solution?.map(
            (solution) => {
              if (solution.manifestId === targetManifest.id) {
                const newVisits = [...solution.visits];

                if (targetPosition >= 0 && manifestLineMoving) {
                  newVisits.splice(targetPosition, 0, {
                    manifestLine: {
                      ...manifestLineMoving,
                      manifestId: targetManifest.manifestId,
                    },
                    position: targetPosition + 1,
                    arrivalTime: "",
                    finishTime: "",
                    longitude: manifestLineMoving.longitude,
                    latitude: manifestLineMoving.latitude,
                    duration: 0,
                  });
                }

                return {
                  ...solution,
                  visits: newVisits.map((visit, index) => {
                    return {
                      ...visit,
                      position: index + 1,
                      manifestLine: {
                        ...visit.manifestLine,
                        position: index,
                      },
                    };
                  }),
                };
              }
              return solution;
            }
          );

          optimizingGroup.unserved = optimizingGroup.unserved?.filter(
            (unserved) => unserved.manifestLine.id !== manifestLineMoving.id
          );
        }
        return { ...optimizingGroup, manuallyChanged: true };
      });
    },
    moveUnallocatedAndUpdateManifests: (
      state,
      action: PayloadAction<{
        manifestLineMoving: manifestLine | manifestLine[];
        newManifests: manifest[];
      }>
    ) => {
      const { manifestLineMoving, newManifests } = action.payload;

      const manifestLinesMoving = Array.isArray(manifestLineMoving)
        ? manifestLineMoving
        : [manifestLineMoving];

      state.unallocatedDocuments = state.unallocatedDocuments.filter(
        (doc) => !manifestLinesMoving.some((line) => line.id === doc.id)
      );

      state.deliveryManifest.unallocatedDocumentCount =
        state.unallocatedDocuments.length;

      state.deliveryManifest.manifests = state.deliveryManifest.manifests.map(
        (manifest) =>
          newManifests.find(
            (newManifest: manifest) =>
              newManifest && newManifest.id === manifest.id
          ) || manifest
      );
    },
    moveAndApplyManifestLine: (state, action: PayloadAction<any>) => {
      const { manifestsUpdate, lineChangeList } = action.payload;

      const finalManifestsUpdate = manifestsUpdate.map((manifest: manifest) => {
        const lines = manifest.manifestLines.map((line: manifestLine) => {
          const lineChangeFound = lineChangeList.find(
            (lineChange: manifestLine) => line.id === lineChange.id
          );
          return {
            ...line,
            timestampManifest: lineChangeFound
              ? lineChangeFound.timestampManifest
              : line.timestampManifest,
          };
        });
        return {
          ...manifest,
          manifestLines: lines,
        };
      });

      state.deliveryManifest = {
        ...state.deliveryManifest,
        manifests: state.deliveryManifest.manifests.map((mnf) => {
          const mnfUpdate = finalManifestsUpdate.find(
            (mnfUd: manifest) => mnfUd.id === mnf.id
          );
          if (mnfUpdate) {
            return mnfUpdate;
          }
          return mnf;
        }),
      };
    },
    changeDriverIntoManifest: (state, action: PayloadAction<any>) => {
      const { manifestChangeDriver } = action.payload;

      state.deliveryManifest = {
        ...state.deliveryManifest,
        manifests: state.deliveryManifest.manifests.map((mnf) => {
          if (mnf.id === manifestChangeDriver.id) {
            return manifestChangeDriver;
          }
          return mnf;
        }),
      };
    },
    updateOptimizeGroupSolutions: (state, action: PayloadAction<any>) => {
      const { jobId, newSolutions } = action.payload;

      state.optimizingGroups = state.optimizingGroups.map((optimizingGroup) => {
        if (optimizingGroup.jobId === jobId) {
          return {
            ...optimizingGroup,
            solution: newSolutions,
            manuallyChanged: true,
          };
        }
        return optimizingGroup;
      });
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAllManifestByDateAsync.pending, (state) => {
        state.loading = true;
        state.loaded = false;
        state.deliveryManifest = {} as deliveryManifest;
        state.optimizingGroups = [] as any;
      })
      .addCase(fetchAllManifestByDateAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchAllManifestByDateAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.deliveryManifest = action.payload;
      })
      .addCase(fetchStorageAddressAsync.pending, (state) => {
        state.storageAddressLoading = true;
      })
      .addCase(fetchStorageAddressAsync.fulfilled, (state, action) => {
        state.storageAddressLoading = false;
        state.storageAddressLoaded = true;
        state.storageAddress = action.payload;
      })
      .addCase(saveStorageAddress.pending, (state) => {
        state.storageAddressLoading = true;
      })
      .addCase(saveStorageAddress.fulfilled, (state, action) => {
        state.storageAddressLoading = false;
        state.storageAddressLoaded = true;
        state.storageAddress = action.payload;
      })
      .addCase(fetchDeliverySettings.pending, (state) => {
        state.deliverySettingsLoading = true;
        state.deliverySettingsLoaded = false;
        state.deliverySettings = {} as deliverySettings;
      })
      .addCase(fetchDeliverySettings.fulfilled, (state, action) => {
        state.deliverySettingsLoading = false;
        state.deliverySettingsLoaded = true;
        state.deliverySettings = action.payload;
      })
      .addCase(getRoutificDefaultSettings.pending, (state) => {
        state.routificSettingsLoading = true;
      })
      .addCase(getRoutificDefaultSettings.rejected, (state) => {
        state.routificSettingsLoading = false;
      })
      .addCase(getRoutificDefaultSettings.fulfilled, (state, action) => {
        state.routificSettingsLoading = false;
        state.routificSettingsLoaded = true;
        state.routificSettings = {
          ...action.payload,
          routeBalanceType: action.payload.minVisitsPerVehicle
            ? "minimumOrders"
            : "orderBalance",
        };
      })
      .addCase(updateRoutificDefaultSettings.pending, (state) => {
        state.routificSettingsLoading = true;
      })
      .addCase(updateRoutificDefaultSettings.rejected, (state) => {
        state.routificSettingsLoading = false;
      })
      .addCase(updateRoutificDefaultSettings.fulfilled, (state, action) => {
        state.routificSettingsLoading = false;
        state.routificSettings = action.payload;
      })

      .addCase(fetchoptimizeByJobIdAsync.pending, (state, action) => {
        console.log("fetchoptimizeByJobIdAsync.pending", state, action);
      })
      .addCase(fetchoptimizeByJobIdAsync.rejected, (state, action) => {
        console.log("fetchoptimizeByJobIdAsync.rejected", state, action);
      })
      .addCase(fetchoptimizeByJobIdAsync.fulfilled, (state, action) => {
        console.log("fetchoptimizeByJobIdAsync.fulfilled", state, action);
      });
  },
});

export const {
  moveRoute,
  clearManifest,
  addManifests,
  updateManifests,
  updateETAIntoManifestItem,
  replaceManifestsUpdatedETAs,
  updateUnallocatedDocuments,
  updateRoutificSettings,
  addOptimizeGroups,
  addOptimizedGroupsInfo,
  discardOptimizeGroup,
  applyOptimizeGroup,
  moveUnallocatedManifestLineInOptimizeGroup,
  moveUnallocatedAndUpdateManifests,
  moveAndApplyManifestLine,
  changeDriverIntoManifest,
  updateOptimizeGroupSolutions,
} = manifestSlice.actions;
export default manifestSlice;
